import { batch, createSignal, onCleanup } from "solid-js";

/**
 * Timings replicated from the approved prototype
 * (`brain/chore/design-system/prototipo-onboarding-success-b.html`).
 */
const LEAD_IN_MS = 600;
const SLOT_MS = 750;
const SUCCESS_DELAY_MS = 400;
const LOG_ACTIVE_OFFSET_MS = 200;

export const BUILD_SLOT_COUNT = 6;
export const BUILD_LOG_COUNT = 3;

export type BuildLogState = "idle" | "active" | "done";

export type ProfileBuildChoreographyOptions = {
  slotCount?: number;
  logCount?: number;
  /**
   * When true the whole stagger is skipped: no timers, no minimum duration.
   * Accepts an accessor so a media-query signal can be read at `start()` time.
   */
  reducedMotion?: boolean | (() => boolean);
  /** Fired once, when both the minimum duration elapsed and the API resolved. */
  onSuccess?: () => void;
};

/**
 * Drives the "profile is being built" phase of the freelance onboarding
 * success screen.
 *
 * The success state is gated on `minimum duration elapsed AND API resolved`:
 * a fast API still plays the full choreography, a slow one keeps the last log
 * entry spinning until it answers. `fail()` aborts immediately — the minimum
 * duration only gates entering success, it never holds back an error.
 */
export const createProfileBuildChoreography = (
  options: ProfileBuildChoreographyOptions = {},
) => {
  const slotCount = options.slotCount ?? BUILD_SLOT_COUNT;
  const logCount = options.logCount ?? BUILD_LOG_COUNT;

  const [revealedSlots, setRevealedSlots] = createSignal(0);
  const [logStates, setLogStates] = createSignal<BuildLogState[]>(
    Array.from({ length: logCount }, () => "idle" as BuildLogState),
  );
  const [phaseDone, setPhaseDone] = createSignal(false);

  let timers: ReturnType<typeof setTimeout>[] = [];
  let started = false;
  let aborted = false;
  let minElapsed = false;
  let resolved = false;

  const clearTimers = () => {
    for (const timer of timers) {
      clearTimeout(timer);
    }
    timers = [];
  };

  const schedule = (delay: number, run: () => void) => {
    timers.push(setTimeout(run, delay));
  };

  const setLogState = (index: number, state: BuildLogState) => {
    setLogStates((current) => {
      const next = [...current];
      next[index] = state;
      return next;
    });
  };

  const enterSuccess = () => {
    if (aborted || phaseDone() || !minElapsed || !resolved) {
      return;
    }
    batch(() => {
      setRevealedSlots(slotCount);
      setLogStates(Array.from({ length: logCount }, () => "done"));
      setPhaseDone(true);
    });
    options.onSuccess?.();
  };

  const start = () => {
    if (started) {
      return;
    }
    started = true;

    const reducedMotion =
      typeof options.reducedMotion === "function"
        ? options.reducedMotion()
        : options.reducedMotion === true;

    if (reducedMotion) {
      // No stagger and no minimum duration, but success is still gated on the
      // API answering: the last log entry stays `active` until it does.
      batch(() => {
        setRevealedSlots(slotCount);
        setLogStates(
          Array.from({ length: logCount }, (_, index) =>
            index === logCount - 1 ? "active" : "done",
          ),
        );
      });
      minElapsed = true;
      enterSuccess();
      return;
    }

    const totalFillMs = LEAD_IN_MS + slotCount * SLOT_MS;
    const logSpanMs = totalFillMs / logCount;

    for (let index = 0; index < slotCount; index++) {
      schedule(LEAD_IN_MS + index * SLOT_MS, () => setRevealedSlots(index + 1));
    }

    for (let index = 0; index < logCount; index++) {
      schedule(index * logSpanMs + LOG_ACTIVE_OFFSET_MS, () =>
        setLogState(index, "active"),
      );
      // The last entry is only completed by the success transition, so a slow
      // API never shows "published" before the server confirmed it.
      if (index < logCount - 1) {
        schedule((index + 1) * logSpanMs, () => setLogState(index, "done"));
      }
    }

    schedule(totalFillMs + SUCCESS_DELAY_MS, () => {
      minElapsed = true;
      enterSuccess();
    });
  };

  const resolve = () => {
    if (aborted) {
      return;
    }
    resolved = true;
    enterSuccess();
  };

  const fail = () => {
    aborted = true;
    clearTimers();
  };

  /** Rewinds the instance so a retry after a failed submit can replay it. */
  const reset = () => {
    clearTimers();
    started = false;
    aborted = false;
    minElapsed = false;
    resolved = false;
    batch(() => {
      setRevealedSlots(0);
      setLogStates(Array.from({ length: logCount }, () => "idle"));
      setPhaseDone(false);
    });
  };

  onCleanup(clearTimers);

  return {
    revealedSlots,
    logStates,
    phaseDone,
    start,
    resolve,
    fail,
    reset,
  };
};

export type ProfileBuildChoreography = ReturnType<
  typeof createProfileBuildChoreography
>;
